import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, ShieldAlert, RefreshCw, PlayCircle, Search, AlertCircle, Zap, Lock } from "lucide-react";
import CreateABHAWizard from "@/components/abdm/CreateABHAWizard";
import VerifyABHAWizard from "@/components/abdm/VerifyABHAWizard";
import ScanShare from "@/components/abdm/ScanShare";

export default function ABDM() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canAbdm = isSuper || user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [patients, setPatients] = useState([]);
  const [consents, setConsents] = useState([]);
  const [txns, setTxns] = useState([]);
  const [dismissedBanner, setDismissedBanner] = useState(() => sessionStorage.getItem("dhoms.abdm.bannerDismissed") === "1");
  const [logFilter, setLogFilter] = useState({ q: "", status: "", method: "" });
  const [linkForm, setLinkForm] = useState({ patient_id: "", abha_input: "", auth_method: "MOBILE_OTP" });
  const [otpStep, setOtpStep] = useState({ txn_id: "", otp: "", awaiting: false });
  const [consentForm, setConsentForm] = useState({ patient_id: "", purpose: "CAREMGT", hi_types: ["OPConsultation", "Prescription", "DiagnosticReport", "DischargeSummary"] });
  const [tokenState, setTokenState] = useState(null);
  const [bridgeUrl, setBridgeUrl] = useState("");
  const [bridgeResp, setBridgeResp] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesResp, setServicesResp] = useState(null);
  const [svcForm, setSvcForm] = useState({ id: "", name: "VYOMRA-HIP", type: "HIP", alias: "vyomra-hip" });
  const [m1, setM1] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get("/abdm/status").then((r) => { setStatus(r.data); setBridgeUrl(r.data.callback_url || ""); setSvcForm((f) => ({ ...f, id: r.data.hip_id || "" })); }).catch(() => setStatus({ configured: false, message: "Unable to fetch ABDM status" }));
    if (isSuper) {
      api.get("/abdm/overview").then((r) => setOverview(r.data)).catch(() => {});
      api.get("/abdm/transactions", { params: { limit: 200 } }).then((r) => setTxns(r.data)).catch(() => {});
    }
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/abdm/consents").then((r) => setConsents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filterLogs = () => {
    api.get("/abdm/transactions", { params: { ...logFilter, limit: 200 } }).then((r) => setTxns(r.data));
  };

  const fetchToken = async () => {
    try {
      const { data } = await api.post("/abdm/token");
      setTokenState(data);
      if (data.ok) toast.success(data.cached ? "Token (cached)" : "Fresh session token from ABDM");
      else toast.error(data.message || "Token fetch failed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const patchBridge = async () => {
    if (!bridgeUrl.startsWith("https://")) return toast.error("Callback URL must be HTTPS");
    try {
      const { data } = await api.patch("/abdm/bridge", { url: bridgeUrl });
      setBridgeResp(data);
      if (data.ok) toast.success("Bridge URL updated on ABDM");
      else toast.error(data.message || `HTTP ${data.http_status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const registerService = async () => {
    if (!svcForm.id || !svcForm.name) return toast.error("Service ID and Name required");
    const payload = [{
      id: svcForm.id,
      name: svcForm.name,
      type: svcForm.type,
      active: true,
      alias: svcForm.alias ? [svcForm.alias] : [],
      endpoints: [{ address: bridgeUrl || (status?.callback_url || ""), connectionType: "https", use: "registration" }],
    }];
    try {
      const { data } = await api.post("/abdm/services", payload);
      setServicesResp(data);
      if (data.ok) toast.success(`${svcForm.type} service registered`);
      else toast.error(data.message || `HTTP ${data.http_status}`);
      loadServices(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const loadServices = async () => {
    try {
      const { data } = await api.get("/abdm/services");
      const list = Array.isArray(data?.response) ? data.response : (Array.isArray(data?.response?.services) ? data.response.services : []);
      setServices(list);
      setServicesResp(data);
    } catch (e) { console.error("ABDM getServices failed:", e); }
  };

  const runM1 = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/abdm/m1-validation");
      setM1(data);
      if (data.ready_for_m1_submission) toast.success(`M1 checklist: ${data.passed}/${data.total} PASSED`);
      else toast.error(`M1 checklist: ${data.passed}/${data.total} PASSED · ${data.failed} FAILED`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };

  const linkABHA = async () => {
    if (!linkForm.patient_id || !linkForm.abha_input) return toast.error("Patient and ABHA number/address required");
    try {
      const { data } = await api.post("/abdm/abha/init-otp", { abha_input: linkForm.abha_input, auth_method: linkForm.auth_method });
      const txn = data?.response?.txnId || data?.response?.transactionId;
      if (data.ok && txn) {
        setOtpStep({ txn_id: txn, otp: "", awaiting: true });
        toast.success("OTP sent via ABDM. Enter the OTP shared with the patient.");
      } else {
        toast.error(data.response?.error?.message || data.message || `HTTP ${data.http_status || 0}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const verifyOTP = async () => {
    if (!otpStep.otp) return toast.error("Enter OTP");
    try {
      const { data } = await api.post("/abdm/abha/verify-otp", {
        txn_id: otpStep.txn_id, otp: otpStep.otp,
        patient_id: linkForm.patient_id, abha_input: linkForm.abha_input,
      });
      if (data.ok) {
        toast.success("ABHA verified & linked to patient");
        setOtpStep({ txn_id: "", otp: "", awaiting: false });
        setLinkForm({ patient_id: "", abha_input: "", auth_method: "MOBILE_OTP" });
      } else {
        toast.error(data.response?.error?.message || `HTTP ${data.http_status || 0}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "OTP verify failed"); }
  };

  const requestConsent = async () => {
    if (!consentForm.patient_id) return toast.error("Select an ABHA-linked patient");
    try {
      const { data } = await api.post("/abdm/consent", consentForm);
      toast.success(`Consent ${data.id.slice(0, 8)} → REQUESTED`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateConsent = async (id, s) => {
    try { await api.put(`/abdm/consent/${id}`, { status: s }); toast.success(`Consent → ${s}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const withABHA = patients.filter((p) => p.abha_number);

  if (!canAbdm) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="max-w-md bg-white border p-6 text-center">
          <Lock className="w-10 h-10 mx-auto text-slate-400 mb-3"/>
          <h2 className="font-display text-lg font-semibold">ABDM Center restricted</h2>
          <p className="text-sm text-slate-500 mt-2">Only Admins and Super Admins can view or configure the ABDM Sandbox integration. Your current role: <b>{user?.role}</b>.</p>
        </div>
      </div>
    );
  }

  const StatusPill = ({ ok, label }) => (
    <div className={`flex items-center gap-2 px-3 py-2 border rounded-sm ${ok ? "bg-teal-50 border-teal-300" : "bg-amber-50 border-amber-300"}`}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-teal-700"/> : <ShieldAlert className="w-4 h-4 text-amber-700"/>}
      <span className="text-xs font-medium">{label}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-semibold">ABDM Center · Vyomra HealthOS</h1>
          <p className="text-sm text-slate-500">Official ABDM Sandbox Milestone 1 — bridge, services and integration audit.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="abdm-reload" className="rounded-sm"><RefreshCw className="w-4 h-4 mr-1"/> Reload</Button>
          <Button onClick={runM1} disabled={running} data-testid="abdm-test-connection" className="bg-blue-700 hover:bg-blue-800 rounded-sm"><Zap className="w-4 h-4 mr-1"/> {running ? "Running..." : "Test ABDM Connection"}</Button>
        </div>
      </div>

      {status?.demo_mode && (
        <div className="border bg-amber-100 border-amber-400 p-3 flex items-center gap-3" data-testid="abdm-demo-banner">
          <Zap className="w-5 h-5 text-amber-700 shrink-0"/>
          <div className="flex-1 text-sm">
            <b className="text-amber-900">DEMO MODE ACTIVE</b>
            <span className="text-amber-800"> · Every ABDM API returns realistic mocked data so you can train reception and demo M1 to auditors without depending on NHA whitelisting. Enter <b>OTP 123456</b> for Aadhaar step, <b>654321</b> for mobile-verify, <b>999999</b> for login. Turn OFF via <code className="bg-amber-200 px-1">ABDM_DEMO_MODE=false</code> once your IP is whitelisted.</span>
          </div>
        </div>
      )}

      {status && !status.configured && !dismissedBanner && (
        <div className="border bg-amber-50 border-amber-300 p-4 flex items-start gap-3" data-testid="abdm-not-configured">
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0"/>
          <div className="flex-1">
            <div className="font-semibold text-amber-900">ABDM Sandbox credentials not configured on this environment.</div>
            <div className="text-xs mt-1 text-amber-800 space-y-1">
              <div>
                <b>On production (dhoms.online):</b> open Emergent → <b>Deployment</b> → <b>Environment Secrets</b> and add the 6 variables below, then click <b>Redeploy</b>. The values from the preview <code>.env</code> file do not ship with a deployment.
              </div>
              <div>
                <b>On preview / local dev:</b> put them in <code className="bg-white px-1">/app/backend/.env</code> and restart the backend.
              </div>
              <ul className="mt-1 font-mono text-[11px] bg-white/70 border rounded-sm p-2 list-none space-y-0.5">
                <li>ABDM_ENV = SANDBOX</li>
                <li>ABDM_BASE_URL = https://dev.abdm.gov.in</li>
                <li>ABDM_CLIENT_ID = &lt;your-sandbox-client-id&gt;</li>
                <li>ABDM_CLIENT_SECRET = &lt;your-sandbox-secret&gt;</li>
                <li>ABDM_HIP_ID = &lt;your-hip-id&gt;</li>
                <li>ABDM_CALLBACK_URL = https://dhoms.online/api/abdm/callback</li>
              </ul>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { sessionStorage.setItem("dhoms.abdm.bannerDismissed", "1"); setDismissedBanner(true); }}
            data-testid="abdm-banner-dismiss"
            className="text-amber-700 hover:text-amber-900 text-xs border border-amber-300 hover:bg-amber-100 rounded-sm px-2 py-1 shrink-0"
            title="Hide this warning for the current browser session"
          >
            Dismiss ×
          </button>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border">
          <TabsTrigger value="overview" data-testid="abdm-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="config" data-testid="abdm-tab-config">Configuration</TabsTrigger>
          <TabsTrigger value="services" data-testid="abdm-tab-services">Services</TabsTrigger>
          <TabsTrigger value="logs" data-testid="abdm-tab-logs">Integration Logs</TabsTrigger>
          <TabsTrigger value="m1" data-testid="abdm-tab-m1">M1 Validation</TabsTrigger>
          <TabsTrigger value="create-abha" data-testid="abdm-tab-create-abha">Create ABHA</TabsTrigger>
          <TabsTrigger value="verify-abha" data-testid="abdm-tab-verify-abha">Verify ABHA</TabsTrigger>
          <TabsTrigger value="scan-share" data-testid="abdm-tab-scan-share">Scan &amp; Share</TabsTrigger>
          <TabsTrigger value="link" data-testid="abdm-tab-link">Link ABHA</TabsTrigger>
          <TabsTrigger value="consent" data-testid="abdm-tab-consent">Consent</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border p-4">
              <div className="label-tiny mb-3">Live Status</div>
              <div className="grid grid-cols-2 gap-2">
                <StatusPill ok={status?.configured} label={`Sandbox: ${status?.env || "-"}`} />
                <StatusPill ok={!!overview?.last_success} label={overview?.last_success ? "Last auth OK" : "Never authenticated"} />
                <StatusPill ok={!!status?.callback_url?.startsWith("https://")} label="HTTPS callback" />
                <StatusPill ok={services.length > 0} label={`${services.length} service(s) registered`} />
                <StatusPill ok={!!overview?.last_callback} label={overview?.last_callback ? "Callback received" : "No callbacks yet"} />
                <StatusPill ok={(overview?.counts?.failed || 0) === 0} label={`${overview?.counts?.failed || 0} failed calls`} />
              </div>
            </div>
            <div className="bg-white border p-4">
              <div className="label-tiny mb-3">Recent Events</div>
              <div className="space-y-2 text-xs">
                <div>
                  <div className="text-slate-500">Last successful sync</div>
                  <div className="font-mono text-teal-800">{overview?.last_success?.created_at || "—"}</div>
                  {overview?.last_success && <div className="text-slate-500 text-[11px]">{overview.last_success.method} · {overview.last_success.url}</div>}
                </div>
                <div>
                  <div className="text-slate-500">Last error</div>
                  <div className="font-mono text-red-800">{overview?.last_error?.created_at || "—"}</div>
                  {overview?.last_error && <div className="text-red-700 text-[11px]">HTTP {overview.last_error.http_status} · {overview.last_error.method} {overview.last_error.url}</div>}
                </div>
                <div>
                  <div className="text-slate-500">Last inbound callback</div>
                  <div className="font-mono text-blue-800">{overview?.last_callback?.created_at || "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* CONFIGURATION */}
        <TabsContent value="config">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border p-4 space-y-3">
              <div className="label-tiny">Bridge Configuration (read-only)</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><Label className="label-tiny">Environment</Label><div className="font-mono">{status?.env || "-"}</div></div>
                <div><Label className="label-tiny">CM-ID</Label><div className="font-mono">{status?.cm_id || "-"}</div></div>
                <div><Label className="label-tiny">Bridge / Base URL</Label><div className="font-mono text-[11px] break-all">{status?.base_url || "-"}</div></div>
                <div><Label className="label-tiny">HIP / Bridge ID</Label><div className="font-mono">{status?.hip_id || "-"}</div></div>
                <div><Label className="label-tiny">Client ID</Label><div className="font-mono">{status?.client_id || "—"}</div></div>
                <div><Label className="label-tiny">Client Secret</Label><div>{status?.client_secret_present ? <Badge className="bg-teal-100 text-teal-800 rounded-sm">Present (server-side)</Badge> : <Badge className="bg-amber-100 text-amber-800 rounded-sm">Missing</Badge>}</div></div>
              </div>
              <div className="text-[11px] text-slate-500 border-t pt-2">Credentials are never exposed to the browser. To rotate, update <code>/app/backend/.env</code> and restart backend.</div>
            </div>

            <div className="bg-white border p-4 space-y-3">
              <div className="label-tiny">Session Token & Bridge Callback</div>
              <Button data-testid="abdm-fetch-token" onClick={fetchToken} disabled={!status?.configured} className="bg-blue-700 hover:bg-blue-800 rounded-sm">Fetch / Refresh Access Token</Button>
              {tokenState && (
                <pre className="text-[11px] bg-slate-50 border p-2 overflow-x-auto whitespace-pre-wrap max-h-40" data-testid="abdm-token-result">{JSON.stringify({
                  ok: tokenState.ok, cached: tokenState.cached, expires_in: tokenState.expires_in,
                  token_preview: tokenState.access_token ? tokenState.access_token.slice(0, 24) + "…" : null,
                  http_status: tokenState.http_status, message: tokenState.message,
                }, null, 2)}</pre>
              )}
              <div className="border-t pt-3 space-y-2">
                <div>
                  <Label className="label-tiny">Callback URL (HTTPS)</Label>
                  <Input data-testid="abdm-bridge-url" value={bridgeUrl} onChange={(e) => setBridgeUrl(e.target.value)} placeholder="https://dhoms.online/api/abdm/callback"/>
                </div>
                <Button onClick={patchBridge} disabled={!status?.configured} data-testid="abdm-patch-bridge" className="bg-blue-700 hover:bg-blue-800 rounded-sm">PATCH Bridge URL to ABDM</Button>
                {bridgeResp && <pre className="text-[11px] bg-slate-50 border p-2 overflow-x-auto whitespace-pre-wrap max-h-40" data-testid="abdm-bridge-result">{JSON.stringify(bridgeResp, null, 2)}</pre>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* SERVICES */}
        <TabsContent value="services">
          <div className="bg-white border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-tiny">HIP / HIU / Health-Locker Service Registration</div>
                <p className="text-xs text-slate-600 mt-1">POST /gateway/v1/bridges/addUpdateServices — registers the service Vyomra HealthOS represents on the ABDM bridge.</p>
              </div>
              <Button size="sm" variant="outline" onClick={loadServices} data-testid="abdm-reload-svcs" className="rounded-sm"><RefreshCw className="w-3 h-3 mr-1"/> Reload from ABDM</Button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="label-tiny">Service ID</Label><Input data-testid="abdm-svc-id" value={svcForm.id} onChange={(e) => setSvcForm({ ...svcForm, id: e.target.value })}/></div>
              <div><Label className="label-tiny">Service Name</Label><Input data-testid="abdm-svc-name" value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })}/></div>
              <div>
                <Label className="label-tiny">Type</Label>
                <select className="w-full h-9 border px-2 text-sm" value={svcForm.type} onChange={(e) => setSvcForm({ ...svcForm, type: e.target.value })} data-testid="abdm-svc-type">
                  <option>HIP</option><option>HIU</option><option>HEALTH_LOCKER</option><option>PHR</option>
                </select>
              </div>
              <div><Label className="label-tiny">Alias</Label><Input value={svcForm.alias} onChange={(e) => setSvcForm({ ...svcForm, alias: e.target.value })}/></div>
            </div>
            <Button onClick={registerService} disabled={!status?.configured} data-testid="abdm-register-svc" className="bg-blue-700 hover:bg-blue-800 rounded-sm">Register / Update Service on ABDM</Button>

            <div className="border-t pt-3">
              <div className="label-tiny mb-2">Registered Services on Bridge ({services.length})</div>
              {services.length > 0 ? (
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">ID</th><th className="label-tiny">Name</th><th className="label-tiny">Type</th><th className="label-tiny">Active</th><th className="label-tiny">Endpoints</th></tr></thead>
                  <tbody>
                    {services.map((s) => (
                      <tr key={s.id} className="border-b align-top" data-testid={`abdm-svc-row-${s.id}`}>
                        <td className="p-2 font-mono text-xs">{s.id}</td>
                        <td className="font-medium">{s.name}</td>
                        <td><Badge className="rounded-sm bg-blue-100 text-blue-800">{s.type}</Badge></td>
                        <td>{s.active ? <CheckCircle2 className="w-4 h-4 text-teal-700"/> : <XCircle className="w-4 h-4 text-red-600"/>}</td>
                        <td className="text-[11px] text-slate-600">{(s.endpoints || []).map((e, k) => <div key={`${s.id}-ep-${k}`}>{e.use}: <span className="font-mono">{e.address}</span></div>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-xs text-slate-500 p-2">No services yet. Fetch a token, then click Reload from ABDM after registering.</div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* LOGS */}
        <TabsContent value="logs">
          <div className="bg-white border">
            <div className="p-3 border-b flex gap-2 items-center flex-wrap">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
                <Input data-testid="abdm-log-search" value={logFilter.q} onChange={(e) => setLogFilter({ ...logFilter, q: e.target.value })} placeholder="URL / Request ID / error text" className="pl-7 h-9"/>
              </div>
              <select className="h-9 border px-2 text-sm" value={logFilter.status} onChange={(e) => setLogFilter({ ...logFilter, status: e.target.value })} data-testid="abdm-log-status">
                <option value="">All statuses</option><option value="success">success</option><option value="error">error</option><option value="exception">exception</option><option value="received">received (callback)</option>
              </select>
              <select className="h-9 border px-2 text-sm" value={logFilter.method} onChange={(e) => setLogFilter({ ...logFilter, method: e.target.value })} data-testid="abdm-log-method">
                <option value="">All methods</option><option>POST</option><option>GET</option><option>PATCH</option><option>CALLBACK</option>
              </select>
              <Button size="sm" onClick={filterLogs} data-testid="abdm-log-filter">Filter</Button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Time</th><th className="label-tiny">Method</th><th className="label-tiny">URL</th><th className="label-tiny">HTTP</th><th className="label-tiny">Status</th><th className="label-tiny">Request ID</th><th className="label-tiny">Error</th></tr></thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-b" data-testid={`abdm-log-${t.id}`}>
                    <td className="p-2 text-[11px]">{new Date(t.created_at).toLocaleString("en-IN")}</td>
                    <td className="text-[11px]"><Badge className="rounded-sm bg-slate-100 text-slate-700">{t.method}</Badge></td>
                    <td className="text-[11px] font-mono truncate max-w-[280px]" title={t.url}>{t.url}</td>
                    <td className="text-[11px] font-mono">{t.http_status || "-"}</td>
                    <td className="text-[11px]"><Badge className={`rounded-sm ${t.status === "success" ? "bg-teal-100 text-teal-800" : t.status === "received" ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800"}`}>{t.status}</Badge></td>
                    <td className="text-[11px] font-mono truncate max-w-[140px]">{t.id.slice(0, 8)}…</td>
                    <td className="text-[11px] text-red-700 truncate max-w-[200px]">{t.error || (t.http_status >= 400 ? "HTTP " + t.http_status : "")}</td>
                  </tr>
                ))}
                {txns.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-slate-500">No integration events yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* M1 VALIDATION */}
        <TabsContent value="m1">
          <div className="bg-white border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-tiny">Milestone 1 Validation Checklist</div>
                <p className="text-xs text-slate-600 mt-1">Runs each step against the live Sandbox and reports the real PASS/FAIL with technical error text — no mock success.</p>
              </div>
              <Button onClick={runM1} disabled={running} data-testid="abdm-run-m1" className="bg-blue-700 hover:bg-blue-800 rounded-sm"><PlayCircle className="w-4 h-4 mr-1"/> {running ? "Running..." : "Run M1 Checklist"}</Button>
            </div>
            {m1 && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-3 mb-3">
                  <Badge className={`rounded-sm ${m1.ready_for_m1_submission ? "bg-teal-100 text-teal-800" : "bg-amber-100 text-amber-800"}`}>{m1.passed}/{m1.total} PASSED</Badge>
                  {m1.ready_for_m1_submission ? <span className="text-sm text-teal-800 font-semibold">Ready for M1 submission to NHA ✓</span> : <span className="text-sm text-amber-800">{m1.failed} step(s) failing — see details below</span>}
                  <span className="ml-auto text-[11px] text-slate-500">run at {new Date(m1.run_at).toLocaleString("en-IN")}</span>
                </div>
                <div className="space-y-2">
                  {m1.steps.map((s, i) => (
                    <div key={`m1-step-${i}`} className={`border p-3 flex items-start gap-3 ${s.status === "PASS" ? "bg-teal-50 border-teal-300" : "bg-red-50 border-red-300"}`} data-testid={`m1-step-${i}`}>
                      {s.status === "PASS" ? <CheckCircle2 className="w-5 h-5 text-teal-700 shrink-0"/> : <AlertCircle className="w-5 h-5 text-red-700 shrink-0"/>}
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">{s.name}</span>
                          <Badge className={`rounded-sm ${s.status === "PASS" ? "bg-teal-100 text-teal-800" : "bg-red-100 text-red-800"}`}>{s.status}</Badge>
                        </div>
                        {s.detail && <div className="text-[11px] text-slate-600 mt-1">{s.detail}</div>}
                        {s.error && <div className="text-[11px] text-red-700 mt-1 font-mono">{s.error}</div>}
                        <div className="text-[10px] text-slate-400 mt-1">{new Date(s.timestamp).toLocaleString("en-IN")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!m1 && <div className="text-sm text-slate-500 p-4 border border-dashed text-center">Click <b>Run M1 Checklist</b> to execute all M1 validations end-to-end.</div>}
          </div>
        </TabsContent>

        {/* CREATE ABHA (M1) */}
        <TabsContent value="create-abha">
          <CreateABHAWizard patients={patients} onLinked={load}/>
        </TabsContent>

        {/* VERIFY ABHA (M1) */}
        <TabsContent value="verify-abha">
          <VerifyABHAWizard patients={patients} onLinked={load}/>
        </TabsContent>

        {/* SCAN & SHARE */}
        <TabsContent value="scan-share">
          <ScanShare/>
        </TabsContent>

        {/* LINK ABHA */}
        <TabsContent value="link">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border p-4 space-y-3">
              <div className="label-tiny">M2 · Verify ABHA via OTP (live ABDM)</div>
              <p className="text-[11px] text-blue-800 bg-blue-50 border border-blue-200 p-2 rounded-sm">
                Uses the official <code>/api/v3/hiecm/user/auth/init</code> and <code>/user/auth/verify</code> endpoints. On successful OTP verification the patient's ABHA is linked and profile is stored server-side.
              </p>
              <div><Label className="label-tiny">Patient</Label>
                <select data-testid="abdm-link-pt" value={linkForm.patient_id} onChange={(e) => setLinkForm({ ...linkForm, patient_id: e.target.value })} className="w-full h-9 border px-2 text-sm" disabled={otpStep.awaiting}>
                  <option value="">-- Select patient --</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.uhid}{p.abha_number ? " · " + p.abha_number : ""}</option>)}
                </select>
              </div>
              <div><Label className="label-tiny">ABHA Number OR Address</Label>
                <Input data-testid="abdm-abha-input" value={linkForm.abha_input} onChange={(e) => setLinkForm({ ...linkForm, abha_input: e.target.value })} placeholder="14-digit ABHA e.g. 91-4567-8901-2345  or  name@abdm" disabled={otpStep.awaiting}/>
              </div>
              <div><Label className="label-tiny">Auth Method</Label>
                <select value={linkForm.auth_method} onChange={(e) => setLinkForm({ ...linkForm, auth_method: e.target.value })} className="w-full h-9 border px-2 text-sm" disabled={otpStep.awaiting} data-testid="abdm-auth-method">
                  <option value="MOBILE_OTP">Mobile OTP</option>
                  <option value="AADHAAR_OTP">Aadhaar OTP</option>
                </select>
              </div>
              {!otpStep.awaiting ? (
                <Button data-testid="abdm-send-otp" onClick={linkABHA} disabled={!status?.configured} className="bg-blue-700 hover:bg-blue-800 rounded-sm">Send OTP</Button>
              ) : (
                <div className="space-y-2 border-t pt-3">
                  <div className="text-[11px] text-teal-800">OTP dispatched via ABDM · txn <span className="font-mono">{otpStep.txn_id.slice(0, 12)}…</span></div>
                  <div><Label className="label-tiny">Enter OTP</Label><Input data-testid="abdm-otp-input" value={otpStep.otp} onChange={(e) => setOtpStep({ ...otpStep, otp: e.target.value })} placeholder="6-digit OTP" maxLength={6}/></div>
                  <div className="flex gap-2">
                    <Button data-testid="abdm-verify-otp" onClick={verifyOTP} className="bg-blue-700 hover:bg-blue-800 rounded-sm">Verify & Link</Button>
                    <Button variant="outline" onClick={() => setOtpStep({ txn_id: "", otp: "", awaiting: false })}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white border">
              <div className="p-3 border-b label-tiny">Patients with ABHA linked ({withABHA.length})</div>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Patient</th><th className="label-tiny">UHID</th><th className="label-tiny">ABHA</th><th className="label-tiny">Verified</th></tr></thead>
                <tbody>
                  {withABHA.map((p) => (
                    <tr key={p.id} className="border-b"><td className="p-2 font-medium">{p.name}</td><td className="font-mono text-xs">{p.uhid}</td><td className="font-mono text-xs">{p.abha_number}</td><td>{p.abha_verified ? <CheckCircle2 className="w-4 h-4 text-teal-700"/> : <XCircle className="w-4 h-4 text-amber-600"/>}</td></tr>
                  ))}
                  {withABHA.length === 0 && <tr><td colSpan="4" className="p-4 text-center text-sm text-slate-500">No patients with ABHA linked yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* CONSENT */}
        <TabsContent value="consent">
          <div className="bg-white border p-4 space-y-2">
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded-sm">Full consent orchestration (request → grant callback → data-transfer) is part of Milestone 2. Consents raised here are stored locally and can be transitioned manually once the ABDM callback flow is live.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><Label className="label-tiny">Patient (ABHA linked)</Label>
                  <select value={consentForm.patient_id} onChange={(e) => setConsentForm({ ...consentForm, patient_id: e.target.value })} className="w-full h-9 border px-2 text-sm">
                    <option value="">-- Select --</option>
                    {withABHA.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.abha_number}</option>)}
                  </select>
                </div>
                <Button onClick={requestConsent} className="bg-blue-700 hover:bg-blue-800 rounded-sm">Request Consent (local record)</Button>
              </div>
              <div>
                <div className="label-tiny mb-2">Consents ({consents.length})</div>
                <div className="max-h-72 overflow-y-auto border">
                  {consents.map((c) => (
                    <div key={c.id} className="p-2 border-b text-xs flex justify-between items-center">
                      <div><div className="font-mono">{c.abha_number}</div><div className="text-slate-500">{c.purpose}</div></div>
                      <div className="flex items-center gap-1">
                        <Badge className="rounded-sm bg-slate-100">{c.status}</Badge>
                        {c.status === "REQUESTED" && <>
                          <button onClick={() => updateConsent(c.id, "GRANTED")} className="text-[10px] px-1.5 py-0.5 border border-teal-300 text-teal-700 rounded-sm">Grant</button>
                          <button onClick={() => updateConsent(c.id, "REJECTED")} className="text-[10px] px-1.5 py-0.5 border border-red-300 text-red-700 rounded-sm">Reject</button>
                        </>}
                      </div>
                    </div>
                  ))}
                  {consents.length === 0 && <div className="p-3 text-xs text-slate-500 text-center">None yet.</div>}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
